import { ErrorHandler, inject, Inject, Injectable, Injector } from '@angular/core'
import { TranslateService } from '@ngx-translate/core'

import { BootstrapData, FetchError } from 'elements-sdk'
import { BOOTSTRAP_DATA } from 'api/providers'
import { LoggerFactory } from 'common/log'
import { ErrorModalComponent } from 'common/util/components/errorModal.component'
import { NotifyService } from 'common/util/services/notify.service'
import { ModalService } from 'common/util.modals'
import { PanelService } from 'panel/services/panel.service'

const NOTIFICATION_THROTTLE = 2000

@Injectable({ providedIn: 'root' })
export class AngularErrorHandler implements ErrorHandler {
  readonly logger = inject(LoggerFactory).create('AngularErrorHandler')

  constructor (
    private injector: Injector,
    @Inject(BOOTSTRAP_DATA) public bootstrapData: BootstrapData,
  ) { }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  handleError (error: any): void {
    if (!this.bootstrapData.debug) {
      this.logger.warn(error)
      return
    }
    this.injector.get(ErrorHandlerService).handleException(error)
  }
}

@Injectable({ providedIn: 'root' })
export class ErrorHandlerService {
  readonly logger = inject(LoggerFactory).create('ErrorHandlerService')
  lastErrorNotification = 0

  constructor (
    private translate: TranslateService,
    private injector: Injector,
    private notify: NotifyService,
    private modal: ModalService,
  ) { }

  handleException (exception: Error, cause?: Error): void {
    if (
      (exception.name === 'AbortError')
      || (exception instanceof FetchError && exception.cause.name === 'AbortError')
    ) {
      // eslint-disable-next-line prefer-rest-params
      this.logger.log(...arguments)
      return
    }

    if (exception.toString().startsWith('Possibly unhandled rejection')) {
      // eslint-disable-next-line prefer-rest-params
      this.logger.warn(...arguments)
      return
    }

    this.logger.error('Unhandled exception occurred')
    if (cause) {
      this.logger.error(cause)
    }
    // eslint-disable-next-line prefer-rest-params
    this.logger.error(...arguments)

    if (
      exception.toString().includes('ExpressionChangedAfterItHasBeenCheckedError')
      || exception.toString().includes('Please use show on visible elements')
    ) {
      return
    }

    let report = '# Exception\n\n'
    report += `${exception.toString()}\n`
    report += '\n# Metadata\n\n'
    report += ` * Time: ${new Date()}\n`
    report += ` * URL: ${location.href}\n`
    report += '\n# Stack\n\n'
    report += `${exception.stack}\n`

    if (Date.now() - this.lastErrorNotification > NOTIFICATION_THROTTLE) {
      this.lastErrorNotification = Date.now()
      if (!this.injector.get(PanelService).active) {
        this.notify.custom(
          'warning',
          this.translate.instant('An error occurred'),
          this.translate.instant('Click to show details'),
          {
            click: () => {
              this.modal.open(ErrorModalComponent, report)
            },
          },
        )
      }
    }
  }
}
